import { ClearableNumberInputWithFrame } from '@flipster/features/src/input/clearable-number-input';
import { css, cx } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { QuoteCurrencyList, getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';

type FocusPositionSizeType = 'qty' | 'amount';

export interface PositionSizeInputProps {
  percent?: number; // 0 ~ 100
  currency?: string;
  quoteCurrency: (typeof QuoteCurrencyList)[number];
  tradeQtyPrecision?: number;
  focusTarget: FocusPositionSizeType;
  qtyValue: string;
  amountValue: string;
  onChangeFocus: (val: FocusPositionSizeType) => unknown;
  onChangeQtyInput: (val: string) => unknown;
  onChangeAmountInput: (val: string) => unknown;
}

export const PositionSizeInput = ({
  percent,
  currency,
  quoteCurrency,
  tradeQtyPrecision = 2,
  focusTarget,
  qtyValue,
  amountValue,
  onChangeFocus,
  onChangeQtyInput,
  onChangeAmountInput,
}: PositionSizeInputProps) => {
  const qtyFocused = focusTarget === 'qty';
  const amountFocused = focusTarget === 'amount';

  return (
    <div className={stack()}>
      <ClearableNumberInputWithFrame
        hideClearButton
        precision={tradeQtyPrecision}
        value={qtyValue}
        min={0}
        onChange={onChangeQtyInput}
        onFocus={() => onChangeFocus('qty')}
        className={cx(
          css({
            borderWidth: '1px 1px 0 !important',
            borderRadius: '8px 8px 0 0 !important',
          }),
          qtyFocused && css({ borderColor: 'border.default.level-4!' }),
        )}
        suffix={
          <>
            {qtyFocused && percent != null && <Percent percent={percent} />}
            <Currency focused={qtyFocused} currency={currency} />
          </>
        }
      />
      <Divider />
      <ClearableNumberInputWithFrame
        hideClearButton
        precision={getQuoteCurrencyPrecision(quoteCurrency)}
        value={amountValue}
        min={0}
        onChange={onChangeAmountInput}
        onFocus={() => onChangeFocus('amount')}
        className={cx(
          css({
            borderWidth: '0 1px 1px !important',
            borderRadius: '0 0 8px 8px !important',
          }),
          amountFocused && css({ borderColor: 'border.default.level-4!' }),
        )}
        suffix={
          <>
            {amountFocused && percent != null && <Percent percent={percent} />}
            <Currency focused={amountFocused} currency={quoteCurrency} />
          </>
        }
      />
    </div>
  );
};

interface PercentProps {
  percent: number;
}
const Percent = ({ percent }: PercentProps) => {
  const percentText = `${Number(isNaN(percent) ? 0 : percent).toFixed(0)}%`;
  const percentStyle = css({
    mr: 8,
    textStyle: 'Text/M/Monospace/Regular',
    color: 'content.default.level-1',
  });
  return (
    <span translate='no' className={percentStyle}>
      {percentText}
    </span>
  );
};

interface CurrencyProps {
  currency?: string;
  focused: boolean;
}
const Currency = ({ currency, focused }: CurrencyProps) => {
  const unfocusedStyle = css({ color: 'content.default.level-2' });
  const focusedStyle = css({ color: 'content.default.level-5' });
  return (
    <span translate='no' className={focused ? focusedStyle : unfocusedStyle}>
      {currency}
    </span>
  );
};

const Divider = () => {
  const dividerStyle = css({
    pos: 'relative',
    w: '100%',
    border: 'none',
    height: 1,
    backgroundColor: 'border.default.level-4',
    _after: {
      pos: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1,
      w: 19,
      h: 19,
      fontSize: 16,
      content: '"≈"',
      color: 'content.default.level-5',
      backgroundColor: 'background.default.level-0',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'border.default.level-4',
      borderRadius: '50%',
    },
  });
  return <div className={dividerStyle} />;
};
